import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next'
import { Box, Chip, IconButton, Stack, Typography } from '@mui/material';
import { AnimatePresence, motion } from "framer-motion"
import { useLocation, useNavigate } from 'react-router-dom';
import { Campaign, ChevronLeft, DragIndicator } from '@mui/icons-material';

const VERT = "#0d6638";
const VERT_FONCE = "#084123";
const ORANGE = "#FF7D12";
const PADDING_BANNIERE = 14;
const TAILLE_ONGLET = 110;
const LARGEUR_MAX_BANNIERE = 720;
const POSITION_TOP = 90;
const POSITION_LEFT = 12;
const PAGE_FINANCE_ISLAMIQUE = "/FinanceIslamique";
const imageBanniere = require("../Database/Files/ProduitsEtServices/Finance Islamique/banniere.jpg");

// La bannière est visible pendant 2 mois à compter de sa mise en ligne (06/10/2026), puis disparaît automatiquement.
const DATE_DEBUT = new Date("2026-10-06T00:00:00");
const DATE_FIN = new Date("2026-12-06T23:59:59");

function BanniereFinanceIslamique() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const [ouverte, setOuverte] = useState(true);
    const [ecran, setEcran] = useState({ largeur: window.innerWidth, hauteur: window.innerHeight });
    const [maintenant, setMaintenant] = useState(Date.now());

    useEffect(() => {
        const redimensionner = () => setEcran({ largeur: window.innerWidth, hauteur: window.innerHeight });
        window.addEventListener("resize", redimensionner);
        const minuteur = setInterval(() => setMaintenant(Date.now()), 60_000);
        return () => { window.removeEventListener("resize", redimensionner); clearInterval(minuteur); };
    }, []);

    if (maintenant < DATE_DEBUT.getTime() || maintenant > DATE_FIN.getTime()) return null;

    const mobile = ecran.largeur < 600;
    const largeur = Math.min(LARGEUR_MAX_BANNIERE, ecran.largeur - 2 * POSITION_LEFT);
    const hauteur = (mobile ? 215 : 130) + 2 * PADDING_BANNIERE;

    const versEvenement = () => {
        const defiler = () => document.getElementById("evenement")?.scrollIntoView({ behavior: "smooth", block: "center" });
        if (pathname === PAGE_FINANCE_ISLAMIQUE) defiler();
        else { navigate(PAGE_FINANCE_ISLAMIQUE); setTimeout(defiler, 600); }
    }

    return (
        // Couche déplaçable : active uniquement quand la bannière est repliée ; elle revient à sa place à l'ouverture.
        <motion.div
            drag={!ouverte}
            dragMomentum={false}
            dragElastic={0.1}
            dragConstraints={{
                left: -POSITION_LEFT,
                top: -POSITION_TOP,
                right: ecran.largeur - POSITION_LEFT - TAILLE_ONGLET,
                bottom: ecran.hauteur - POSITION_TOP - TAILLE_ONGLET
            }}
            animate={ouverte ? { x: 0, y: 0 } : undefined}
            whileDrag={{ scale: 1.05, cursor: "grabbing" }}
            style={{ position: "fixed", top: POSITION_TOP, left: POSITION_LEFT, zIndex: 5, touchAction: "none" }}
        >
            {/* Couche flottante (va-et-vient vertical) */}
            <motion.div
                animate={{ y: [0, -8, 0] }}
                transition={{ repeat: Infinity, duration: 3.5, ease: "easeInOut" }}
            >
                <motion.div
                    initial={false}
                    animate={{ width: ouverte ? largeur : TAILLE_ONGLET, height: ouverte ? hauteur : TAILLE_ONGLET, borderRadius: ouverte ? 20 : TAILLE_ONGLET / 2 }}
                    transition={{ type: "spring", stiffness: 140, damping: 20 }}
                    style={{
                        overflow: "hidden", position: "relative", transformOrigin: "left center",
                        color: "white", background: `linear-gradient(90deg, ${VERT_FONCE}, ${VERT})`,
                        boxShadow: "0 12px 35px rgba(8,65,35,.35)", border: `2px solid ${ORANGE}`, boxSizing: "border-box"
                    }}
                >
                    {/* Onglet replié : image ronde + badge (déplaçable, un tap l'ouvre) */}
                    <AnimatePresence>
                        {!ouverte && (
                            <motion.button
                                key="onglet"
                                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: .2 }}
                                onTap={() => setOuverte(true)}
                                aria-label={t("FINANCE_ISLAMIQUE.banniere.ouvrir")}
                                title={t("FINANCE_ISLAMIQUE.banniere.ouvrir")}
                                style={{ position: "absolute", inset: 0, border: "none", padding: 0, cursor: "grab", background: "transparent" }}
                            >
                                <img src={imageBanniere} alt="" draggable={false} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", pointerEvents: "none" }} />
                                <motion.span
                                    animate={{ scale: [1, 1.2, 1] }} transition={{ repeat: Infinity, duration: 1.6 }}
                                    style={{ position: "absolute", top: 4, right: 4, width: 26, height: 26, borderRadius: "50%", background: ORANGE, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 0 0 3px white" }}
                                >
                                    <Campaign sx={{ fontSize: 16, color: "white" }} />
                                </motion.span>
                                <span style={{ position: "absolute", bottom: 6, left: "50%", transform: "translateX(-50%)", background: "rgba(8,65,35,.75)", borderRadius: 12, padding: "1px 6px", display: "flex" }}>
                                    <DragIndicator sx={{ fontSize: 16, color: "white" }} />
                                </span>
                            </motion.button>
                        )}
                    </AnimatePresence>

                    {/* Contenu déplié : toute la zone est cliquable */}
                    <motion.div
                        role="link"
                        tabIndex={ouverte ? 0 : -1}
                        title={t("FINANCE_ISLAMIQUE.banniere.bouton")}
                        onClick={versEvenement}
                        onKeyDown={(e) => { if (e.key === "Enter") versEvenement() }}
                        animate={{ opacity: ouverte ? 1 : 0, x: ouverte ? 0 : -30 }}
                        transition={{ duration: .35, delay: ouverte ? .15 : 0 }}
                        style={{ display: "flex", alignItems: "stretch", height: hauteur - 4, width: largeur, padding: PADDING_BANNIERE, boxSizing: "border-box", cursor: "pointer", pointerEvents: ouverte ? "auto" : "none" }}
                    >
                        <Box sx={{ width: mobile ? 72 : 150, flexShrink: 0, position: "relative", borderRadius: 3, overflow: "hidden" }}>
                            <img src={imageBanniere} alt="" draggable={false} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                            <Box sx={{ position: "absolute", inset: 0, background: `linear-gradient(90deg, transparent 60%, ${VERT_FONCE})` }} />
                        </Box>
                        <Box flex={1} textAlign="left" sx={{ display: "flex", flexDirection: "column", justifyContent: "center", px: mobile ? 1.25 : 2, minWidth: 0 }}>
                            <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                                <Chip size="small" label={t("FINANCE_ISLAMIQUE.banniere.badge")} sx={{ bgcolor: ORANGE, color: "white", fontWeight: "bold", height: 20, fontSize: ".7rem" }} />
                                <Typography fontWeight="bold" sx={{ fontSize: mobile ? ".85rem" : "1rem", lineHeight: 1.25 }}>{t("FINANCE_ISLAMIQUE.banniere.titre")}</Typography>
                            </Stack>
                            <Typography variant="body2" sx={{ color: "white", opacity: .95, mt: .75, fontSize: mobile ? ".74rem" : ".875rem", lineHeight: 1.4, ...(mobile ? {} : { display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }) }}>
                                <Trans i18nKey="FINANCE_ISLAMIQUE.banniere.texte" components={{ b: <b style={{ fontWeight: "bold" }} /> }} />
                            </Typography>
                            <Box mt={mobile ? 1.25 : 1.5}>
                                <motion.button
                                    onClick={(e) => { e.stopPropagation(); versEvenement() }}
                                    whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.95 }}
                                    className='btn' style={{ whiteSpace: "nowrap", padding: "6px 16px", fontSize: ".8rem" }}>
                                    {t("FINANCE_ISLAMIQUE.banniere.bouton")}
                                </motion.button>
                            </Box>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: mobile ? "flex-start" : "center", pl: .5 }}>
                            <IconButton aria-label={t("FINANCE_ISLAMIQUE.banniere.fermer")} title={t("FINANCE_ISLAMIQUE.banniere.fermer")} onClick={(e) => { e.stopPropagation(); setOuverte(false) }} size="small" sx={{ color: "white", bgcolor: "rgba(255,255,255,.12)", "&:hover": { bgcolor: ORANGE } }}>
                                <ChevronLeft />
                            </IconButton>
                        </Box>
                    </motion.div>
                </motion.div>
            </motion.div>
        </motion.div>
    )
}

export default BanniereFinanceIslamique
